import { supabase } from '../supabase';

export interface BackendApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  // Machine-readable code from the edge function body (e.g. 'no_opted_in_recipients'),
  // populated by invokeFunction when a FunctionsHttpError carries a JSON body.
  error_code?: string;
  // Full parsed error body, for callers that need fields beyond error/error_code.
  details?: Record<string, unknown>;
  message?: string;
}

export interface CampaignDetails {
  id: string;
  name: string;
  status: 'draft' | 'ready' | 'pending_confirmation' | 'active' | 'sending' | 'paused' | 'completed' | 'failed';
  workspace_id: string;
  created_at: string;
  updated_at: string;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
  subject: string;
  sender_email?: string;
  sender_name?: string;
  scheduled_at?: string;
  completed_at?: string;
}

export interface CampaignCreationResponse {
  campaign_id: string;
  status: 'active' | 'scheduled' | 'pending_confirmation' | 'draft';
  timeline: {
    start_time: string;
    estimated_completion: string;
    total_emails: number;
    days_to_complete: number;
  };
  confirmation?: {
    requires_confirmation: boolean;
    recipient_count: number;
    safety_warnings: string[];
    cooldown_violations: Array<{
      recipient: string;
      last_sent: string;
      hours_remaining: number;
    }>;
    confirmation_token: string;
  };
  warnings?: string[];
  conflicts?: Array<{
    account: string;
    occupied_slots: number;
    next_available: string;
  }>;
}

export interface CampaignCreationRequest {
  name: string;
  workspace_id: string;
  subject_template: string;
  body_template: string;
  email_account_id: string;
  recipients: Array<{
    email: string;
    first_name?: string;
    last_name?: string;
    company?: string;
    // Per-recipient overrides from the wizard Review-step inline edit. When
    // present (non-blank), campaign-scheduler stores this already-merged text
    // verbatim on the recipient's queue job instead of rendering the campaign
    // template — i.e. a one-off tweak for a single recipient. Absent for every
    // un-edited recipient (the default merge path is unchanged).
    rendered_subject_override?: string;
    rendered_body_override?: string;
    [key: string]: any;
  }>;
  scheduled_start?: string;
  skip_confirmation?: boolean;
  force_activate?: boolean;
  scheduling_mode?: 'after_existing' | 'prioritize' | 'balanced';
  // Minutes between sends; clamped server-side to >= 15 (the per-account floor).
  send_interval_minutes?: number;
  draft_mode?: boolean;
  // Client-supplied idempotency key; a retried create returns the existing
  // campaign instead of creating a duplicate (matters for single-shot active create).
  idempotency_key?: string;
}

export interface CampaignProgress {
  campaign_id: string;
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  pending_count: number;
  last_sent_at?: string;
  estimated_completion?: string;
  current_status: string;
  safety_warnings: SafetyWarning[];
}

export interface SafetyWarning {
  id: string;
  type: 'rate_limit' | 'quota_exceeded' | 'spam_detected' | 'bounce_rate' | 'cooldown_violation' | 'safety_control';
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  created_at: string;
  requires_action: boolean;
}

export interface QueueStats {
  total_jobs: number;
  pending_jobs: number;
  processing_jobs: number;
  failed_jobs: number;
  completed_jobs: number;
  avg_processing_time: number;
  last_processed_at?: string;
}

export interface QueueJob {
  id: string;
  campaign_id: string;
  recipient_email: string;
  status: 'pending' | 'processing' | 'sent' | 'failed' | 'retrying';
  attempts: number;
  max_attempts: number;
  error_message?: string;
  scheduled_at: string;
  processed_at?: string;
  created_at: string;
}

export interface SafetyControl {
  id: string;
  type: 'global_pause' | 'workspace_pause' | 'account_pause' | 'rate_limit';
  target_id?: string;
  reason: string;
  created_at: string;
  created_by: string;
  is_active: boolean;
}

export interface SafetyStatus {
  is_healthy: boolean;
  active_controls: SafetyControl[];
  recent_anomalies: SafetyWarning[];
  system_status: 'operational' | 'degraded' | 'maintenance' | 'emergency';
}

export interface CampaignTimeline {
  campaign_id: string;
  estimated_start: string;
  estimated_completion: string;
  total_duration_hours: number;
  send_intervals: {
    interval_minutes: number;
    batch_size: number;
    safety_tier: 'optimal' | 'caution' | 'restricted';
  }[];
}

export interface ValidationResult {
  valid: boolean;
  violations: Array<{
    rule: string;
    severity: 'warning' | 'error' | 'critical';
    message: string;
    suggested_action?: string;
  }>;
  health_score?: number;
  should_pause?: boolean;
  retry_after?: number;
}

class EdgeFunctionsApi {
  private async invokeFunction<T>(
    functionName: string, 
    body: any = {}
  ): Promise<BackendApiResponse<T>> {
    try {
      console.log(`Invoking Edge Function: ${functionName}`, body);
      
      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
      });

      if (error) {
        // supabase-js returns a FunctionsHttpError for any non-2xx whose .message
        // is the generic "Edge Function returned a non-2xx status code". The
        // actual payload — { error, error_code, ... } — lives on error.context
        // (the Response). Parse it so callers, toasts, and GlitchTip see the
        // real reason ("No recipients are opted in to marketing") instead of
        // the wrapper. Falls back to error.message if the body isn't JSON.
        let message = error.message || 'Edge Function invocation failed';
        let errorCode: string | undefined;
        let details: Record<string, unknown> | undefined;
        const ctx = (error as any)?.context;
        if (ctx && typeof ctx.json === 'function') {
          try {
            const parsed = await ctx.json();
            if (parsed && typeof parsed === 'object') {
              if (typeof parsed.error === 'string' && parsed.error) message = parsed.error;
              if (typeof parsed.error_code === 'string') errorCode = parsed.error_code;
              details = parsed;
            }
          } catch {
            // Body wasn't JSON; keep the generic message.
          }
        }
        console.error(`Edge Function ${functionName} error:`, message, errorCode);
        return {
          success: false,
          error: message,
          error_code: errorCode,
          details,
        };
      }

      console.log(`Edge Function ${functionName} response:`, data);

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error(`Edge Function ${functionName} exception:`, error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  // Health & Monitoring
  async healthCheck(): Promise<BackendApiResponse<{ status: string; timestamp: string }>> {
    // For Edge Functions, we'll check Supabase connection directly
    try {
      const { error } = await supabase.from('hermes_email_campaigns').select('id').limit(1);
      
      if (error) {
        return {
          success: false,
          error: `Database connection failed: ${error.message}`,
        };
      }

      return {
        success: true,
        data: {
          status: 'healthy',
          timestamp: new Date().toISOString(),
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Health check failed',
      };
    }
  }

  async testWebhook(): Promise<BackendApiResponse<{ webhook_status: string }>> {
    const testEndpoint = 'TESTING_MODE' // Environment variable will be read by Edge Function
    return {
      success: true,
      data: {
        webhook_status: testEndpoint ? 'configured' : 'not_configured',
      },
    };
  }

  // Campaign Management
  async createCampaign(campaignData: CampaignCreationRequest): Promise<BackendApiResponse<CampaignCreationResponse>> {
    return this.invokeFunction('campaign-scheduler', campaignData);
  }

  async getCampaigns(workspaceId: string): Promise<BackendApiResponse<CampaignDetails[]>> {
    try {
      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .select(`
          id,
          name,
          status,
          workspace_id,
          created_at,
          updated_at,
          recipient_count,
          emails_sent,
          emails_failed,
          emails_pending,
          subject_template,
          scheduled_start,
          scheduled_end
        `)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const campaigns: CampaignDetails[] = (data || []).map(campaign => ({
        id: campaign.id,
        name: campaign.name,
        status: campaign.status,
        workspace_id: campaign.workspace_id,
        created_at: campaign.created_at,
        updated_at: campaign.updated_at,
        total_recipients: campaign.recipient_count,
        sent_count: campaign.emails_sent || 0,
        failed_count: campaign.emails_failed || 0,
        pending_count: campaign.emails_pending || 0,
        subject: campaign.subject_template,
        scheduled_at: campaign.scheduled_start,
        completed_at: campaign.scheduled_end,
      }));

      return {
        success: true,
        data: campaigns,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch campaigns',
      };
    }
  }

  async getCampaignDetails(campaignId: string): Promise<BackendApiResponse<CampaignDetails>> {
    try {
      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .select(`
          *,
          email_account:hermes_email_accounts!inner(email_address)
        `)
        .eq('id', campaignId)
        .single();

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const campaign: CampaignDetails = {
        id: data.id,
        name: data.name,
        status: data.status,
        workspace_id: data.workspace_id,
        created_at: data.created_at,
        updated_at: data.updated_at,
        total_recipients: data.recipient_count,
        sent_count: data.emails_sent || 0,
        failed_count: data.emails_failed || 0,
        pending_count: data.emails_pending || 0,
        subject: data.subject_template,
        sender_email: data.email_account?.email_address,
        scheduled_at: data.scheduled_start,
        completed_at: data.scheduled_end,
      };

      return {
        success: true,
        data: campaign,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch campaign details',
      };
    }
  }

  async getCampaignProgress(campaignId: string): Promise<BackendApiResponse<CampaignProgress>> {
    try {
      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const progress: CampaignProgress = {
        campaign_id: data.id,
        total_recipients: data.recipient_count,
        sent_count: data.emails_sent || 0,
        failed_count: data.emails_failed || 0,
        pending_count: data.emails_pending || 0,
        current_status: data.status,
        estimated_completion: data.scheduled_end,
        safety_warnings: [], // TODO: Fetch from safety logs
      };

      return {
        success: true,
        data: progress,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch campaign progress',
      };
    }
  }

  async pauseCampaign(campaignId: string): Promise<BackendApiResponse<{ message: string }>> {
    try {
      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .update({ status: 'paused' })
        .eq('id', campaignId)
        .select()
        .single();

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: true,
        data: { message: `Campaign '${data.name}' has been paused` },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to pause campaign',
      };
    }
  }

  async resumeCampaign(campaignId: string): Promise<BackendApiResponse<{ message: string }>> {
    try {
      // Reschedule-from-scratch on resume: the RPC re-paces this campaign's
      // pending jobs uniformly from now (behind the active queue), then flips it
      // to active — atomically. A bare status flip would replay the campaign's
      // stale job times and jump the queue. The RPC also fires the CAN-SPAM
      // activation gate, so a sender without a mailing address is blocked here.
      const { data, error } = await supabase.rpc('resume_campaign_rescheduled', {
        p_campaign_id: campaignId,
      });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const row = Array.isArray(data) ? data[0] : data;
      const n = (row?.rescheduled_count as number) ?? 0;
      return {
        success: true,
        data: { message: `Campaign resumed — ${n} email${n === 1 ? '' : 's'} re-paced from now.` },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to resume campaign',
      };
    }
  }

  async getCampaignTimeline(campaignId: string): Promise<BackendApiResponse<CampaignTimeline>> {
    try {
      const { data, error } = await supabase
        .from('hermes_email_campaigns')
        .select('scheduled_start, scheduled_end, recipient_count')
        .eq('id', campaignId)
        .single();

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const startTime = new Date(data.scheduled_start);
      const endTime = new Date(data.scheduled_end);
      const durationHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

      const timeline: CampaignTimeline = {
        campaign_id: campaignId,
        estimated_start: data.scheduled_start,
        estimated_completion: data.scheduled_end,
        total_duration_hours: durationHours,
        send_intervals: [
          {
            interval_minutes: 15,
            batch_size: 1,
            safety_tier: 'optimal',
          },
        ],
      };

      return {
        success: true,
        data: timeline,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch campaign timeline',
      };
    }
  }

  // Queue Management
  async getQueueStats(): Promise<BackendApiResponse<QueueStats>> {
    try {
      const { data, error } = await supabase
        .from('hermes_email_queue_jobs')
        .select('status, created_at, sent_at')
        .order('created_at', { ascending: false });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const stats: QueueStats = {
        total_jobs: data.length,
        pending_jobs: data.filter(job => job.status === 'pending').length,
        processing_jobs: data.filter(job => job.status === 'processing').length,
        failed_jobs: data.filter(job => job.status === 'failed').length,
        completed_jobs: data.filter(job => job.status === 'sent').length,
        avg_processing_time: 0, // TODO: Calculate from send_at - created_at
        last_processed_at: data.find(job => job.sent_at)?.sent_at,
      };

      return {
        success: true,
        data: stats,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch queue stats',
      };
    }
  }

  async retryFailedJobs(campaignId?: string): Promise<BackendApiResponse<{ retried_count: number }>> {
    try {
      let query = supabase
        .from('hermes_email_queue_jobs')
        .update({ 
          status: 'pending', 
          error_message: null,
          scheduled_send_time: new Date().toISOString()
        })
        .eq('status', 'failed');

      if (campaignId) {
        query = query.eq('campaign_id', campaignId);
      }

      const { data, error } = await query.select();

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: true,
        data: { retried_count: data?.length || 0 },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to retry failed jobs',
      };
    }
  }

  async getWorkspaceJobs(workspaceId: string, status?: string): Promise<BackendApiResponse<QueueJob[]>> {
    try {
      let query = supabase
        .from('hermes_email_queue_jobs')
        .select('*')
        .eq('workspace_id', workspaceId);

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query
        .order('scheduled_send_time', { ascending: false })
        .limit(200);

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const jobs: QueueJob[] = (data || []).map(job => ({
        id: job.id,
        campaign_id: job.campaign_id,
        recipient_email: job.recipient_email,
        status: job.status,
        attempts: job.attempts,
        max_attempts: job.max_attempts,
        error_message: job.error_message,
        scheduled_at: job.scheduled_send_time,
        processed_at: job.sent_at,
        created_at: job.created_at,
      }));

      return {
        success: true,
        data: jobs,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch workspace jobs',
      };
    }
  }

  async getCampaignJobs(campaignId: string, status?: string): Promise<BackendApiResponse<QueueJob[]>> {
    try {
      let query = supabase
        .from('hermes_email_queue_jobs')
        .select('*')
        .eq('campaign_id', campaignId);

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query.order('scheduled_send_time', { ascending: true });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const jobs: QueueJob[] = (data || []).map(job => ({
        id: job.id,
        campaign_id: job.campaign_id,
        recipient_email: job.recipient_email,
        status: job.status,
        attempts: job.attempts,
        max_attempts: job.max_attempts,
        error_message: job.error_message,
        scheduled_at: job.scheduled_send_time,
        processed_at: job.sent_at,
        created_at: job.created_at,
      }));

      return {
        success: true,
        data: jobs,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch campaign jobs',
      };
    }
  }

  async getOverdueJobs(): Promise<BackendApiResponse<QueueJob[]>> {
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('hermes_email_queue_jobs')
        .select('*')
        .eq('status', 'pending')
        .lt('scheduled_send_time', now)
        .order('scheduled_send_time', { ascending: true });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const jobs: QueueJob[] = (data || []).map(job => ({
        id: job.id,
        campaign_id: job.campaign_id,
        recipient_email: job.recipient_email,
        status: job.status,
        attempts: job.attempts,
        max_attempts: job.max_attempts,
        error_message: job.error_message,
        scheduled_at: job.scheduled_send_time,
        processed_at: job.sent_at,
        created_at: job.created_at,
      }));

      return {
        success: true,
        data: jobs,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch overdue jobs',
      };
    }
  }

  // Safety System
  async getSafetyStatus(): Promise<BackendApiResponse<SafetyStatus>> {
    return this.invokeFunction('safety-validator', {
      type: 'system_health'
    });
  }

  async getSafetyControls(): Promise<BackendApiResponse<SafetyControl[]>> {
    try {
      // NOTE: do not filter on is_active here. SafetyControlsManager splits the
      // result into Active vs History tabs client-side; filtering server-side
      // makes the History tab permanently empty.
      const { data, error } = await supabase
        .from('hermes_system_safety_controls')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: true,
        data: data || [],
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch safety controls',
      };
    }
  }

  async createSafetyControl(
    type: SafetyControl['type'],
    reason: string,
    targetId?: string
  ): Promise<BackendApiResponse<SafetyControl>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('hermes_system_safety_controls')
        .insert({
          control_type: type,
          target_id: targetId,
          reason: reason,
          triggered_by: user?.id,
          is_active: true,
        })
        .select()
        .single();

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create safety control',
      };
    }
  }

  async deactivateSafetyControl(controlId: string): Promise<BackendApiResponse<{ message: string; account_flag_cleared?: boolean }>> {
    // Route through the safety-controls-admin edge function. It enforces
    // workspace-admin / superadmin auth based on the control_type, writes
    // audit rows, and (for account_pause) clears emergency_pause_active on
    // the linked hermes account when the last active control is dropped.
    const result = await this.invokeFunction<{
      control_id: string;
      deactivated?: boolean;
      already_inactive?: boolean;
      account_flag_cleared?: boolean;
    }>('safety-controls-admin', {
      action: 'deactivate',
      control_id: controlId,
    });

    if (!result.success) {
      return {
        success: false,
        error: result.error || 'Failed to deactivate safety control',
        error_code: result.error_code,
        details: result.details,
      };
    }

    return {
      success: true,
      data: {
        message: 'Safety control deactivated successfully',
        account_flag_cleared: result.data?.account_flag_cleared ?? false,
      },
    };
  }

  async getRecentAnomalies(hours?: number): Promise<BackendApiResponse<SafetyWarning[]>> {
    try {
      const hoursAgo = new Date(Date.now() - (hours || 24) * 60 * 60 * 1000).toISOString();
      
      const { data, error } = await supabase
        .from('hermes_safety_anomaly_logs')
        .select('*')
        .gte('created_at', hoursAgo)
        .order('created_at', { ascending: false });

      if (error) {
        return {
          success: false,
          error: error.message,
        };
      }

      const warnings: SafetyWarning[] = (data || []).map(anomaly => ({
        id: anomaly.id,
        type: anomaly.anomaly_type,
        message: JSON.stringify(anomaly.details),
        severity: anomaly.severity,
        created_at: anomaly.created_at,
        requires_action: anomaly.severity === 'critical',
      }));

      return {
        success: true,
        data: warnings,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch recent anomalies',
      };
    }
  }

  // Safety Validation
  async validateCampaignCreation(
    workspaceId: string,
    emailAccountId: string,
    recipientCount: number
  ): Promise<BackendApiResponse<ValidationResult>> {
    return this.invokeFunction('safety-validator', {
      type: 'campaign_creation',
      workspace_id: workspaceId,
      email_account_id: emailAccountId,
      recipient_data: {
        count: recipientCount
      }
    });
  }
}

export const emailBackendApi = new EdgeFunctionsApi();